import { useState } from "react"
import type { ReactNode } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { CompanyClaim } from "../types"
import type { CompanyDirectoryEntry } from "../utils/segment-connections"

import { ArrowLeft, Play } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

import { RelatedCompaniesRow } from "./related-companies-row"
import {
  ConfidenceBadge,
  EmptyTabText,
  SectionCard,
  SourceLinks,
} from "./segment-detail-primitives"
import { SegmentPendingChanges } from "./segment-pending-changes"
import {
  decideCompanyChanges,
  getCompanyPending,
  getCompanyReport,
  getCompanyRun,
  startCompanyRun,
} from "../api/company-analysis-api"
import { COMPANY_NAME } from "../utils/branding"

const VERIFICATION_STYLES: Record<string, string> = {
  verified: "bg-success/15 text-success",
  contradicted: "bg-destructive/15 text-destructive",
  unverified: "bg-warning/15 text-warning-foreground",
}

/** Dossier-open cascade (§ #2063): header first, then each section fades-up with
 * the portfolio grid's stagger idiom (segment-card-enter honours reduced-motion). */
function CascadeIn({ index, children }: { index: number; children: ReactNode }) {
  return (
    <div className="segment-card-enter" style={{ "--segment-index": index } as React.CSSProperties}>
      {children}
    </div>
  )
}

/** Layout-mirroring placeholder while the report query is in flight (§ #2063) —
 * prevents the sparse "No dossier yet" flash on every dossier open. */
function DossierSkeleton() {
  return (
    <div aria-busy="true" className="space-y-5">
      {[0, 1, 2].map((index) => (
        <div key={index} className="border-border bg-card rounded-lg border p-4 shadow-sm">
          <Skeleton className="mb-3 h-3 w-28" />
          <Skeleton className="mb-2 h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ))}
    </div>
  )
}

function ClaimRow({ claim }: { claim: CompanyClaim }) {
  const status = claim.verification?.status ?? "unverified"
  const citation = claim.citations?.[0]
  return (
    <div className="border-border/60 rounded-md border p-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-foreground text-sm">{claim.text}</p>
        <span
          className={cn(
            "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase",
            VERIFICATION_STYLES[status] ?? VERIFICATION_STYLES.unverified
          )}
        >
          {status}
        </span>
      </div>
      {citation ? (
        <p className="text-muted-foreground mt-1 text-xs italic">
          “{citation.quote}” <span className="not-italic">({citation.sourceId})</span>
        </p>
      ) : null}
    </div>
  )
}

export function CompanyDossier({
  canDecide,
  canRun,
  companyId,
  directory = [],
  fromSegmentId,
  fromSegmentName,
  hiddenSections,
  onBack,
  onOpenCompany,
}: {
  canDecide: boolean
  /** May start a company run from `fromSegmentId` (admin, or owner of that segment). */
  canRun: boolean
  companyId: string
  directory?: CompanyDirectoryEntry[]
  fromSegmentId?: string
  fromSegmentName?: string
  /** User-hidden dossier sections (#2069 D2): profile|moves|fit|relationship|claims. */
  hiddenSections?: string[]
  onBack?: () => void
  onOpenCompany?: (name: string, segmentId: string) => void
}) {
  const queryClient = useQueryClient()
  const [runStatus, setRunStatus] = useState<string | null>(null)

  const reportQuery = useQuery({
    queryKey: ["sa-company-report", companyId],
    queryFn: () => getCompanyReport(companyId),
  })
  const pendingQuery = useQuery({
    queryKey: ["sa-company-pending", companyId],
    queryFn: () => getCompanyPending(companyId),
  })

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ["sa-company-report", companyId] })
    void queryClient.invalidateQueries({ queryKey: ["sa-company-pending", companyId] })
  }

  const runMutation = useMutation({
    mutationFn: async () => {
      const started = await startCompanyRun(companyId, { fromSegmentId, mode: "full" })
      let current = started
      for (
        let i = 0;
        i < 40 && current.status !== "completed" && current.status !== "failed";
        i += 1
      ) {
        await new Promise((resolve) => setTimeout(resolve, 3000))
        current = await getCompanyRun(companyId, started.runId)
        setRunStatus(current.status)
      }
      return current
    },
    onSuccess: () => {
      setRunStatus(null)
      invalidate()
    },
    onError: () => setRunStatus(null),
  })

  const decideMutation = useMutation({
    mutationFn: (input: { decision: "approve" | "reject"; changeId?: string }) =>
      decideCompanyChanges(companyId, input),
    onSuccess: invalidate,
  })

  const company = reportQuery.data?.company
  const report = reportQuery.data?.report
  const claims = report?.grounded?.claims ?? []
  const isRunning = runMutation.isPending
  const displayName = company?.name ?? companyId
  const sectionHidden = new Set(hiddenSections ?? [])

  return (
    <section className="space-y-5">
      <header className="space-y-2">
        {onBack ? (
          <button
            className="text-muted-foreground hover:text-foreground inline-flex max-w-full items-center gap-1 text-xs font-medium transition"
            title={fromSegmentName ? `Back to ${fromSegmentName}` : undefined}
            type="button"
            onClick={onBack}
          >
            <ArrowLeft className="size-3.5 shrink-0" />
            {/* Long segment names get ellipsised (#2073 C-5) — full name in the title. */}
            <span className="max-w-[48ch] truncate">
              {fromSegmentName ? `Back to ${fromSegmentName}` : "Back"}
            </span>
          </button>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            {/* While loading, `displayName` would fall back to the raw company id
                slug — show a name-shaped skeleton instead (§ #2063). */}
            {reportQuery.isPending ? (
              <>
                <Skeleton className="h-6 w-44" />
                <Skeleton className="mt-1.5 h-3 w-20" />
              </>
            ) : (
              <>
                <h3 className="text-foreground text-lg font-semibold">{displayName}</h3>
                {company?.role ? (
                  <p className="text-muted-foreground text-xs capitalize">{company.role}</p>
                ) : null}
              </>
            )}
          </div>
          {/* Run is offered only to users who may run this company's segment (Nolato
              #15), matching SegmentDetail's Run gate; the backend still enforces it. */}
          {canRun ? (
            <Button disabled={isRunning} size="sm" onClick={() => runMutation.mutate()}>
              <Play className="mr-1 size-4" />
              {isRunning ? `Analyzing… ${runStatus ?? ""}` : "Run analysis"}
            </Button>
          ) : null}
        </div>
      </header>

      <RelatedCompaniesRow
        companyName={displayName}
        directory={directory}
        onOpenCompany={onOpenCompany}
      />

      {reportQuery.isPending ? <DossierSkeleton /> : null}
      {!reportQuery.isPending && report ? (
        <div className="space-y-5">
          {sectionHidden.has("profile") ? null : (
            <CascadeIn index={0}>
              <SectionCard title="Profile">
                <p
                  className={cn(
                    "text-sm",
                    report.profile?.summary ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {report.profile?.summary ||
                    "No company profile yet — run an analysis to build one."}
                </p>
                <p className="text-muted-foreground mt-2 text-xs">
                  {[
                    report.profile?.hq,
                    report.profile?.employees,
                    report.profile?.revenue,
                    report.profile?.ownership,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "No firmographics yet"}
                </p>
                <SourceLinks sources={report.profile?.sources ?? []} />
              </SectionCard>
            </CascadeIn>
          )}

          {sectionHidden.has("moves") ? null : (
            <CascadeIn index={1}>
              <SectionCard title="Recent moves">
                {(report.recentMoves ?? []).length === 0 ? (
                  <EmptyTabText>No recent moves found.</EmptyTabText>
                ) : (
                  <div className="grid gap-3">
                    {(report.recentMoves ?? []).map((move, index) => (
                      <article
                        key={`${move.title}-${index}`}
                        className="bg-muted/50 rounded-md p-3"
                      >
                        <h4 className="text-foreground text-sm font-semibold">
                          {move.title}
                          {move.flag ? (
                            <span className="bg-primary/10 text-primary ml-2 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase">
                              {move.flag}
                            </span>
                          ) : null}
                        </h4>
                        <p className="text-muted-foreground mt-1 text-sm">{move.summary}</p>
                        <SourceLinks sources={move.sources ?? []} />
                      </article>
                    ))}
                  </div>
                )}
              </SectionCard>
            </CascadeIn>
          )}

          {sectionHidden.has("fit") ? null : (
            <CascadeIn index={2}>
              <SectionCard title={`Fit to ${COMPANY_NAME}`}>
                {report.companyFit?.summary ? (
                  <div className="flex items-center gap-2">
                    <p className="text-foreground text-sm">{report.companyFit.summary}</p>
                    {report.companyFit.confidence ? (
                      <ConfidenceBadge confidence={report.companyFit.confidence} />
                    ) : null}
                  </div>
                ) : (
                  <EmptyTabText>
                    No fit assessment yet — run an analysis to evaluate this company against{" "}
                    {COMPANY_NAME}.
                  </EmptyTabText>
                )}
              </SectionCard>
            </CascadeIn>
          )}

          {sectionHidden.has("relationship") ? null : (
            <CascadeIn index={3}>
              <SectionCard title="Relationship & outreach">
                {report.relationship?.summary || report.outreach?.angle ? (
                  <>
                    {report.relationship?.status ? (
                      <p className="text-muted-foreground text-xs uppercase">
                        {report.relationship.status}
                      </p>
                    ) : null}
                    {report.relationship?.summary ? (
                      <p className="text-foreground mt-1 text-sm">{report.relationship.summary}</p>
                    ) : null}
                    {report.outreach?.angle ? (
                      <p className="text-foreground mt-3 text-sm">
                        <span className="font-semibold">Angle:</span> {report.outreach.angle}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <EmptyTabText>No relationship history yet — run an analysis.</EmptyTabText>
                )}
              </SectionCard>
            </CascadeIn>
          )}

          {sectionHidden.has("claims") ? null : (
            <CascadeIn index={4}>
              <SectionCard title={`Grounded claims (${claims.length})`}>
                {claims.length === 0 ? (
                  <EmptyTabText>No grounded claims yet — run an analysis.</EmptyTabText>
                ) : (
                  <div className="grid gap-2">
                    {claims.map((claim) => (
                      <ClaimRow key={claim.id} claim={claim} />
                    ))}
                  </div>
                )}
              </SectionCard>
            </CascadeIn>
          )}
        </div>
      ) : null}
      {!reportQuery.isPending && !report ? (
        <EmptyTabText>No dossier yet. Run an analysis to build one.</EmptyTabText>
      ) : null}

      <SegmentPendingChanges
        canDecide={canDecide}
        pending={
          pendingQuery.data ? { segmentId: companyId, changes: pendingQuery.data.changes } : null
        }
        onDecide={(decision, changeId) => decideMutation.mutate({ decision, changeId })}
      />
    </section>
  )
}
